/**
 * Helper functions for selecting images from contentPhotos array
 */

// Device breakpoints mapping
const DEVICE_BREAKPOINTS = {
  SM: '(max-width: 640px)',
  MD: '(min-width: 641px) and (max-width: 1024px)',
  LG: '(min-width: 1025px) and (max-width: 1920px)',
  TV: '(min-width: 1921px)'
};

// Device priority order (fallback chain)
const DEVICE_PRIORITY = ['LG', 'MD', 'SM', 'TV'];

/**
 * Detects current device category based on screen width
 * @returns {string} Device category (SM, MD, LG, TV)
 */
export const detectDevice = () => {
  if (typeof window === 'undefined') return 'LG'; // SSR fallback

  const width = window.innerWidth;

  if (width <= 640) return 'SM';
  if (width <= 1024) return 'MD';
  if (width <= 1920) return 'LG';
  return 'TV';
};

/**
 * Finds the best matching image from contentPhotos array
 * @param {Array} contentPhotos - Array of image objects
 * @param {string} type - Image type (e.g., 'horizontalCover', 'verticalCover', 'videoPlayerCover')
 * @param {string} [preferredDevice] - Optional device override
 * @returns {Object|null} Best matching image object or null
 */
export const selectImageByType = (contentPhotos, type, preferredDevice = null) => {
  if (!contentPhotos || !Array.isArray(contentPhotos) || !type) {
    return null;
  }

  // Filter by type first
  const matchingType = contentPhotos.filter(photo => photo.type === type);

  if (matchingType.length === 0) {
    return null;
  }

  // If only one image of this type, return it
  if (matchingType.length === 1) {
    return matchingType[0];
  }

  const targetDevice = preferredDevice || detectDevice();

  // Try to find exact device match
  const exactMatch = matchingType.find(photo => photo.device === targetDevice);
  if (exactMatch) {
    return exactMatch;
  }

  // Fallback to device priority order
  for (const device of DEVICE_PRIORITY) {
    const fallbackMatch = matchingType.find(photo => photo.device === device);
    if (fallbackMatch) {
      return fallbackMatch;
    }
  }

  // Last resort: return first available
  return matchingType[0];
};

/**
 * Gets all available image types from contentPhotos
 * @param {Array} contentPhotos - Array of image objects
 * @returns {Array} Array of unique image types
 */
export const getAvailableImageTypes = (contentPhotos) => {
  if (!contentPhotos || !Array.isArray(contentPhotos)) {
    return [];
  }

  return [...new Set(contentPhotos.map(photo => photo.type))];
};

/**
 * Gets responsive sizes string based on image type
 * @param {string} type - Image type
 * @returns {string} Responsive sizes attribute
 */
export const getResponsiveSizes = (type) => {
  const sizeMap = {
    horizontalCover: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    verticalCover: '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
    videoPlayerCover: '(max-width: 640px) 80vw, (max-width: 1024px) 60vw, 40vw',
    default: '100vw'
  };

  return sizeMap[type] || sizeMap.default;
};

/**
 * Creates a media query string for the current device
 * @param {string} device - Device category
 * @returns {string} CSS media query
 */
export const getDeviceMediaQuery = (device) => {
  return DEVICE_BREAKPOINTS[device] || DEVICE_BREAKPOINTS.LG;
};